// The ONE place the Xano instance host is defined.
// Empty-safe on purpose: `??` would let a blank `VITE_XANO_BASE_URL=` line in an
// env file override the fallback with "" and break every API call.
// Lives here (a zero-import leaf module) rather than in api/config.ts so that
// src/shared/* can import it without depending on the src/api layer.
// 2026-07-26: Qbank's backend moved off its own instance (xqu6-rxcf-7ifo) into the
// Aksanio instance as workspace 2. The API group canonicals are unchanged, so only
// the host moves. The old instance is being switched off — do not point back at it.
export const XANO_HOST = import.meta.env.VITE_XANO_BASE_URL || 'https://xnf0-oeoz-xw8o.f2.xano.io';

export const ROUTES = {
  HOME: '/',
  ONBOARDING: '/onboarding',
  DASHBOARD: '/dashboard',
  ANALYTICS: '/analytics',
  DEMO: '/demo',
  QUESTIONS_MANAGEMENT: '/questions',
  QUESTION_GENERATOR: '/questions/generate',
  QUESTION_DEMO: '/question-demo',
  EXAM: '/exam',
  EXAM_HISTORY: '/exam-history',
  EXAM_SESSION: (sessionId: string) => `/exam/session/${sessionId}`,
  EXAM_RESULTS: (sessionId: string) => `/exam/results/${sessionId}`,
  REVIEW: '/review',
  BUDGET: '/budget',
  FINANCIAL: '/financial',
  USERS: '/users',
  SETTINGS: '/settings',
  ADMIN: '/admin',
} as const;

// Xano API Group Canonical IDs
export const XANO_API_GROUPS = {
  AUTH: 'IvFHo60M',          // Authentication & Main API
  EXAMS: 'JH3i_0To',         // Exams API
  PROMPTING: 'rTsLzfwn',     // AI Prompting & Management
  MAIN: 'h-QoQY2S',          // Main API (legacy)
  TAGS_TOPICS: 'VnKpOm4a',   // Tags & Topics
  AI_PROMPTING: 'N3mMQwGD',  // Prompting (newer)
  STRIPE: 'GeBgOY5W',        // Stripe Payments & Subscriptions
} as const;

export const SPECIALTIES = [
  { value: 'cardiology', label: 'Cardiology' },
  { value: 'neurology', label: 'Neurology' },
  { value: 'pathology', label: 'Pathology' },
  { value: 'radiology', label: 'Radiology' },
  { value: 'emergency_medicine', label: 'Emergency Medicine' },
  { value: 'internal_medicine', label: 'Internal Medicine' },
  { value: 'surgery', label: 'Surgery' },
] as const;

export const QUESTION_TYPES = [
  { value: 'single_choice', label: 'Single Choice' },
  { value: 'multiple_select', label: 'Multiple Select' },
  { value: 'true_false', label: 'True/False' },
] as const;

export const DIFFICULTY_LEVELS = [
  { value: 1, label: 'Very Easy' },
  { value: 2, label: 'Easy' },
  { value: 3, label: 'Medium' },
  { value: 4, label: 'Hard' },
  { value: 5, label: 'Very Hard' },
] as const;

export const TARGET_AUDIENCES = [
  { value: 'medical_student', label: 'Medical Student' },
  { value: 'resident', label: 'Resident' },
  { value: 'board_exam', label: 'Board Exam' },
  { value: 'continuing_education', label: 'Continuing Education' },
] as const;

export const BUDGET_WARNING_LEVELS = {
  SAFE: { threshold: 0.6, color: 'success' },
  CAUTION: { threshold: 0.8, color: 'warning' },
  WARNING: { threshold: 0.9, color: 'warning' },
  CRITICAL: { threshold: 0.95, color: 'error' },
  OVER: { threshold: 1, color: 'error' },
} as const;

// User Role Definitions
export const USER_ROLES = {
  ADMIN: 'Admin',
  SENIOR_REVIEWER: 'Senior Reviewer',
  REVIEWER: 'Reviewer',
  USER: 'User',
} as const;

// Role IDs from Xano database
export const ROLE_IDS = {
  [USER_ROLES.ADMIN]: 1,
  [USER_ROLES.SENIOR_REVIEWER]: 2,
  [USER_ROLES.REVIEWER]: 3,
  [USER_ROLES.USER]: 4,
} as const;

// Reverse mapping for easy lookup
export const ROLE_ID_TO_NAME = {
  1: USER_ROLES.ADMIN,
  2: USER_ROLES.SENIOR_REVIEWER,
  3: USER_ROLES.REVIEWER,
  4: USER_ROLES.USER,
} as const;

// Role display options for UI components
export const ROLE_OPTIONS = [
  { value: USER_ROLES.ADMIN, label: 'Administrator', protected: true },
  { value: USER_ROLES.SENIOR_REVIEWER, label: 'Senior Reviewer', protected: false },
  { value: USER_ROLES.REVIEWER, label: 'Reviewer', protected: false },
  { value: USER_ROLES.USER, label: 'User', protected: false },
] as const;

// Helper function to get role name from ID
export function getRoleFromId(roleId: number): UserRole {
  return ROLE_ID_TO_NAME[roleId as keyof typeof ROLE_ID_TO_NAME] || USER_ROLES.USER;
}

// Helper function to get role ID from name
export function getRoleId(roleName: string): number {
  return ROLE_IDS[roleName as keyof typeof ROLE_IDS] || ROLE_IDS[USER_ROLES.USER];
}

// Type definitions
export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];
export type RoleId = typeof ROLE_IDS[keyof typeof ROLE_IDS];

// Permission definitions
export const PERMISSIONS = {
  // Page access
  VIEW_DASHBOARD: [USER_ROLES.USER, USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_QUESTION_DEMO: [USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_EXAM: [USER_ROLES.USER, USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_EXAM_HISTORY: [USER_ROLES.USER, USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_QUESTIONS_MANAGEMENT: [USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_REVIEW_QUEUE: [USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_USERS: [USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_FINANCIAL: [USER_ROLES.ADMIN],
  VIEW_BUDGET: [USER_ROLES.ADMIN],
  VIEW_ADMIN: [USER_ROLES.ADMIN],
  
  // Feature access
  GENERATE_BATCHES: [USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  REVIEW_QUESTIONS: [USER_ROLES.REVIEWER, USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  VIEW_BUDGET_IN_GENERATION: [USER_ROLES.SENIOR_REVIEWER, USER_ROLES.ADMIN],
  MANAGE_USERS: [USER_ROLES.ADMIN],
  EDIT_USER_ROLES: [USER_ROLES.ADMIN], // Only Admins can change user roles
  MANAGE_FINANCES: [USER_ROLES.ADMIN],
} as const;

// Helper function to check permissions
export function hasPermission(userRole: string | undefined, permission: keyof typeof PERMISSIONS): boolean {
  if (!userRole) return false;
  return PERMISSIONS[permission].includes(userRole as any);
}